import {useApolloClient, useMutation} from "@apollo/client";
import {useState} from "react";
import toast from "react-hot-toast";
import {FaEllipsis} from "react-icons/fa6";
import {useLocation, useNavigate} from "react-router";

import {DeleteActionDialog} from "@/components/deleteActionDialog/DeleteActionDialog.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {
  DropdownAction,
  DropdownActionItem,
  DropdownItem,
  DropDownReactLink,
} from "@/components/quickActionsBar/DropdownAction.tsx";
import {DropdownDivider} from "@/components/quickActionsBar/DropdownDivider.tsx";
import {GQL_CHECK_DETAILS, GQL_DELETE_CHECKS} from "@/lib/queries/checks.ts";
import {Service} from "@/lib/types/checks.ts";
import {GraphQLResponse, ResponseStatus} from "@/lib/types/common.ts";
import {CheckDeleteDialogBody} from "@/pages/checks/components/actions/CheckDeleteDialogBody.tsx";
import {QUERY} from "@/pages/checks/constants.ts";
import {BulkSetContactsModal} from "@/pages/checks/list/actions/bulkSetContacts/BulkSetContactsModal.tsx";
import {BulkSetEscalationsModal} from "@/pages/checks/list/actions/bulkSetEscalations/BulkSetEscalationsModal.tsx";
import {BulkSetIntervalsModal} from "@/pages/checks/list/actions/bulkSetIntervals/BulkSetIntervalsModal.tsx";
import {BulkSetLocationsModal} from "@/pages/checks/list/actions/bulkSetLocations/BulkSetLocationsModal.tsx";
import {BulkSetSRTModal} from "@/pages/checks/list/actions/bulkSetSensitivityRetriesAndTimeout/BulkSetSRTModal.tsx";
import {BulkSetSlaModal} from "@/pages/checks/list/actions/bulkSetSLA/BulkSetSlaModal.tsx";
import {BulkSetTagsModal} from "@/pages/checks/list/actions/bulkSetTags/BulkSetTagsModal.tsx";
import {BulkUpgradeCheckerVersionModal} from "@/pages/checks/list/actions/bulkUpgradeCheckerVersion/BulkUpgradeCheckerVersionModal.tsx";

interface CheckMoreActionsProps {
  service: Service;
}

export const CheckMoreActions = ({service}: CheckMoreActionsProps) => {
  const client = useApolloClient();
  const location = useLocation();
  const navigate = useNavigate();

  const [showSetLocations, setShowSetLocations] = useState(false);
  const [showSetIntervals, setShowSetIntervals] = useState(false);
  const [showSetContacts, setShowSetContacts] = useState(false);
  const [showSetSensitivity, setShowSetSensitivity] = useState(false);
  const [showSetSla, setShowSetSla] = useState(false);
  const [showSetEscalations, setShowSetEscalations] = useState(false);
  const [showSetTags, setShowSetTags] = useState(false);
  const [showUpgradeCheckerVersion, setShowUpgradeCheckerVersion] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [deleteCheck, {loading: deleteLoading, data: deleteData, reset: resetDelete}] = useMutation<
    GraphQLResponse<null>
  >(GQL_DELETE_CHECKS, {
    onError: () => toast.error("Something went wrong, please contact support"),
  });

  const deleteErrors =
    deleteData?.response.status === ResponseStatus.ERROR
      ? Object.values(deleteData.response.message as Record<string, unknown>)
          .filter(Array.isArray)
          .flat()
      : [];

  const selectedItemsIds = [service.id];
  const onSuccess = () => void client.refetchQueries({include: [GQL_CHECK_DETAILS]});

  const dropdownItems: DropdownItem[] = [
    <DropdownActionItem text="Set Location" onClick={() => setShowSetLocations(true)} />,
    <DropdownActionItem text="Set Intervals" onClick={() => setShowSetIntervals(true)} />,
    <DropdownDivider />,
    <DropdownActionItem text="Set Contacts" onClick={() => setShowSetContacts(true)} />,
    <DropdownActionItem text="Set Sensitivity" onClick={() => setShowSetSensitivity(true)} />,
    <DropdownActionItem text="Set SLA" onClick={() => setShowSetSla(true)} />,
    <DropdownActionItem text="Set Escalations" onClick={() => setShowSetEscalations(true)} />,
    <DropdownActionItem text="Set Tags" onClick={() => setShowSetTags(true)} />,
    <DropdownActionItem text="Upgrade Checker Versions" onClick={() => setShowUpgradeCheckerVersion(true)} />,
    <DropdownDivider />,
    <DropDownReactLink
      text="Duplicate Check"
      to={`/checks/${service.id}/edit?${QUERY.DUPLICATE}=true`}
      state={{from: location}}
    />,
    <DropdownActionItem text="Delete" onClick={() => setShowDeleteConfirm(true)} loading={deleteLoading} />,
  ];

  return (
    <>
      <DropdownAction
        id={`check-analysis-more-${service.id}`}
        items={dropdownItems}
        buttonVariant="secondary"
        buttonSize="extraSmall"
        moreActionsText={null}
        buttonContent={
          <>
            <Icon icon={FaEllipsis} />
            More
          </>
        }
      />
      <BulkSetLocationsModal
        show={showSetLocations}
        setShow={setShowSetLocations}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkSetIntervalsModal
        show={showSetIntervals}
        setShow={setShowSetIntervals}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkSetContactsModal
        show={showSetContacts}
        setShow={setShowSetContacts}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkSetSRTModal
        show={showSetSensitivity}
        setShow={setShowSetSensitivity}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkSetSlaModal
        show={showSetSla}
        setShow={setShowSetSla}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkSetEscalationsModal
        show={showSetEscalations}
        setShow={setShowSetEscalations}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkSetTagsModal
        show={showSetTags}
        setShow={setShowSetTags}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkUpgradeCheckerVersionModal
        show={showUpgradeCheckerVersion}
        setShow={setShowUpgradeCheckerVersion}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <DeleteActionDialog
        show={showDeleteConfirm}
        title="Delete Check"
        loading={deleteLoading}
        onCancel={() => {
          setShowDeleteConfirm(false);
          resetDelete();
        }}
        onConfirm={async () => {
          const {data} = await deleteCheck({variables: {ids: selectedItemsIds}});
          if (data?.response.status === ResponseStatus.SUCCESS) {
            setShowDeleteConfirm(false);
            resetDelete();
            toast.success("Check has been removed");
            navigate("/checks");
          }
        }}
      >
        <CheckDeleteDialogBody name={service.name} serviceType={service.monitoring_service_type} />
        <FormErrorAlerts errors={deleteErrors} />
      </DeleteActionDialog>
    </>
  );
};
